import 'react-table-6/react-table.css'
import React, { Component } from 'react'
import ReactTable from 'react-table-6'
import Cell from './Cell'
import { setNarrative } from '../actions/narrative'
import { connect } from 'react-redux'
import ButtonPanel from './ButtonPanel'
import Comments from './Comments'
import { setModal } from '../actions/appSettings'
import EditMode from './EditMode'

class Table extends Component {
  componentWillMount() {
    this.deserializeTableIntoState(this.props.narrative.table)
  }

  componentWillReceiveProps(nextProps, nextContext) {
    this.deserializeTableIntoState(nextProps.narrative.table)
  }

  onTableLostFocus = event => {
    // event.currentTarget is div surrounding ReactTable component, event.relatedTarget is control about to get focus
    const focusHasLeftTable = !event.currentTarget.contains(event.relatedTarget)
    if (focusHasLeftTable) {
      this.serializeTableToStore()
    }
  }

  deserializeTableIntoState(table) {
    this.setState({ data: JSON.parse(table)['table-data'] })
  }

  serializeTableToStore = () => {
    const serializedTable = JSON.stringify({ 'table-data': this.state.data })
    this.props.setNarrative({ ...this.props.narrative, table: serializedTable })
  }

  validateUnder500Modal = sum => {
    this.props.setModal(`Columns A to C cannot add up to more than 500. Proposed total is ${sum}`)
  }

  validateUnder100Modal = (column, newValue) => {
    this.props.setModal(`Column ${column.toUpperCase()} cannot be more than 100. Proposed value is ${newValue}`)
  }

  changeCellValue = (event, index, column) => {
    if (event.target.type === 'number') {
      const newValue = event.target.value
      if (this.isTableValid(column, index, newValue)) {
        // Is valid, so permit update
        this.setState(({ data }) => ({
          data: [
            ...data.slice(0, index),
            {
              ...data[index],
              [column]: { ...data[index][column], value: newValue },
            },
            ...data.slice(index + 1),
          ],
        }))
      } else {
        // invalid, so roll back change
        event.target.value = this.state.data[index][column].value
      }
    } else if (event.target.type === 'checkbox') {
      const newChecked = event.target.checked
      this.setState(({ data }) => ({
        data: [
          ...data.slice(0, index),
          {
            ...data[index],
            [column]: {
              ...data[index][column],
              available: !newChecked,
              value: newChecked ? null : 0,
            },
          },
          ...data.slice(index + 1),
        ],
      }))
    }
  }

  isTableValid = (column, index, newValue) => {
    const columnsMaxTotal500 = ['a', 'b1', 'b2', 'c']
    const columnsMaxEach100 = ['d', 'e']

    // If editing one of cols A-C, validate that columns A to C add up to 500 or less (CN-51)
    if (columnsMaxTotal500.indexOf(column) > -1) {
      let proposedSum = columnsMaxTotal500.reduce((sum, columnName, idx, arr) => {
        return (
          sum +
          parseInt(
            column === columnName
              ? newValue
              : this.state.data[index][columnName].available
              ? this.state.data[index][columnName].value
              : 0,
            10
          )
        )
      }, 0)

      if (proposedSum > 500) {
        this.validateUnder500Modal(proposedSum)
        return false
      }

      // If editing col D or E, validate that entered value <= 100
      // (Users requested modal rather than setting max attribute on input that silently blocks)
    } else if (columnsMaxEach100.includes(column)) {
      if (this.state.data[index][column].available && newValue > 100) {
        this.validateUnder100Modal(column, newValue)
        return false
      }
    } // (else)
    return true
  }

  widthCol1Pre2020 = 350
  widthCol1Post2020 = 300
  widthCol2 = 80
  widthCol3Pre2020 = 350
  widthCol3Post2020 = 300
  widthColRest = 100

  // Note: columns must be declared outside of the render loop so that prior controls still exist in
  // the DOM for React diff to compare to decide whether to re-render. Else re-renders everything.
  columnsPre2020 = [
    { Header: 'Strategic Outcome', accessor: 'so', width: this.widthCol1Pre2020 },
    {
      Header: 'Country Activity Code',
      id: 'cac',
      accessor: d => d.cac,
      width: this.widthCol2,
    },
    { Header: 'Activity Description', accessor: 'ad', width: this.widthCol3Pre2020 },
    {
      Header: 'A - Prioritized Beneficiaries (%)',
      accessor: 'a',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.a.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.a.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'B1 - Prioritized Rations - Food Transfer (%)',
      accessor: 'b1',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.b1.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.b1.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'B2 - Prioritized Rations - CBT rations (%)',
      accessor: 'b2',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.b2.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.b2.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'C - Prioritized Assistance Days/Months (%)',
      accessor: 'c',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.c.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.c.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'D - Prioritized activities for Capacity Strengthening and/or Service Delivery (%)',
      accessor: 'd',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.d.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.d.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
  ]

  columnsPost2020 = [
    { Header: 'Strategic Outcome', accessor: 'so', width: this.widthCol1Post2020 },
    {
      Header: 'Country Activity Code',
      id: 'cac',
      accessor: d => d.cac,
      width: this.widthCol2,
    },
    { Header: 'Activity Description', accessor: 'ad', width: this.widthCol3Post2020 },
    {
      Header: 'A - Prioritized Beneficiaries (%)',
      accessor: 'a',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.a.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.a.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'B1 - Prioritized Rations - Food Transfer (%)',
      accessor: 'b1',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.b1.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.b1.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'B2 - Prioritized Rations - CBT rations (%)',
      accessor: 'b2',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.b2.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.b2.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'C - Prioritized Assistance Days/Months (%)',
      accessor: 'c',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.c.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.c.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'D - Prioritized activities for Capacity Strengthening  (%)',
      accessor: 'd',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.d.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.d.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
    {
      Header: 'E - Prioritized activities for Service Delivery (%)',
      accessor: 'e',
      width: this.widthColRest,
      Cell: row => (
        <Cell
          available={row.original.e.available}
          is_editable={this.props.narrative.user_can_save}
          value={row.original.e.value}
          onChange={evt => this.changeCellValue(evt, row.index, row.column.id)}
        />
      ),
    },
  ]

  render() {
    return (
      <div className='activitiesTableContainer'>
        <EditMode />
        {this.props.narrative.user_can_save && (
          <div className='text-danger' style={{ textAlign: 'center' }}>
            Please click "n/a" if the columns are not applicable to the activity OR indicate "0" if the activity is
            completely deprioritized.
          </div>
        )}
        <div onBlur={this.onTableLostFocus}>
          <ReactTable
            className={'activitiesTable'}
            data={this.state.data}
            columns={'e' in this.state.data[0] ? this.columnsPost2020 : this.columnsPre2020}
            defaultPageSize={this.state.data.length}
            showPagination={false}
            sortable={false}
            resizable={false}
            getTdProps={(state, rowInfo, column, instance) => {
              const cellData = this.state.data[rowInfo.index][column.id]
              if (
                rowInfo.index > 0 &&
                column.id === 'so' &&
                cellData === this.state.data[rowInfo.index - 1][column.id]
              ) {
                return { className: 'hidden-cell' }
              }
              return {}
            }}
          />
        </div>

        <Comments />
        <ButtonPanel wrapperClassName='activitiesTable--submission' />
      </div>
    )
  }
}

const mapStateToProps = state => ({
  narrative: state.narrative.narrative,
  userRole: state.appSettings.userInfo ? state.appSettings.userInfo.role : null,
})

const mapDispatchToProps = { setNarrative, setModal }

Table = connect(
  mapStateToProps,
  mapDispatchToProps
)(Table)
export default Table
